import { useTexture } from '@react-three/drei';
import { SIDE } from 'containers/world/sceneController/cube360/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import useAlphaMask from './useAlphaMask';
import { useFrame } from '@react-three/fiber';

// Load textures for cube faces from src/assets/images/marker/Overview 01 using a side->filename map
// sideToFileName example: { px: 'right.png', nx: 'left.png', ... }
export const createFaceTexturesFromMap = async (
  sideToFileName = {},
  basePath = ''
) => {
  const results = {};
  const promises = SIDE.map(async (side) => {
    const fileName = sideToFileName[side];
    if (!fileName) return;
    const url = `${basePath}/${encodeURIComponent(fileName)}`;
    // const texture = await cleanLoadTexture(url);
    // texture.flipY = false;
    results[side] = url;
  });

  await Promise.all(promises);
  return results;
};

function MarkerFace({ side, imgUrl, transform, onClicked }) {
  const meshRef = useRef();

  const texture = useTexture(imgUrl);

  const mask = useAlphaMask(texture);

  const matRef = useRef();
  const animRef = useRef(1);

  // Attach alpha mask data to mesh
  useEffect(() => {
    if (
      meshRef.current &&
      mask &&
      mask.alphaData &&
      mask.width &&
      mask.height
    ) {
      meshRef.current.dataAlpha = mask.alphaData;
      meshRef.current.dataWidth = mask.width;
      meshRef.current.dataHeight = mask.height;
    }
  }, [mask]);

  // Handle click with alpha mask filter
  const handleClick = useCallback(
    (e) => {
      if (!meshRef.current) return;
      const { dataAlpha, dataWidth, dataHeight } = meshRef.current;
      if (!dataAlpha) return;
      const uv = e.uv;
      const x = Math.floor(uv.x * dataWidth);
      const y = Math.floor((1 - uv.y) * dataHeight);
      const alpha = dataAlpha[(y * dataWidth + x) * 4 + 3];
      if (alpha > 100) {
        onClicked(side, e);
      }
    },
    [onClicked, side]
  );

  useFrame((state, delta) => {
    if (animRef.current === 1) {
      const newOpacity = meshRef.current.material.opacity + delta;
      if (newOpacity > 0.7) {
        animRef.current = -1;
        meshRef.current.material.opacity = 0.7;
      } else {
        meshRef.current.material.opacity = newOpacity;
      }
    } else {
      const newOpacity = meshRef.current.material.opacity - delta;
      if (newOpacity < 0) {
        animRef.current = 1;
        meshRef.current.material.opacity = 0;
      } else {
        meshRef.current.material.opacity = newOpacity;
      }
    }
  });

  return (
    <mesh
      ref={meshRef}
      position={transform.position}
      rotation={transform.rotation}
      onClick={handleClick}
      scale-x={-1}
      renderOrder={2}
    >
      <planeGeometry args={[1000, 1000]} />
      <meshStandardMaterial
        ref={matRef}
        map={texture}
        transparent
        color={'white'}
        opacity={1}
        alphaTest={0.01}
        side={THREE.DoubleSide}
        attach="material"
      />
    </mesh>
  );
}

const MarkerComponent = ({ imageMap, basePath, onFaceClicked = () => {} }) => {
  const distance = 499;
  const [textures, setTextures] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const loaded = await createFaceTexturesFromMap(
          imageMap || {},
          basePath
        );
        if (alive) setTextures(loaded);
      } catch (e) {
        // noop
      }
    })();
    return () => {
      alive = false;
    };
  }, [basePath, imageMap]);

  const faceTransforms = {
    px: { position: [distance, 0, 0], rotation: [0, -Math.PI / 2, 0] },
    nx: { position: [-distance, 0, 0], rotation: [0, Math.PI / 2, 0] },
    py: { position: [0, distance, 0], rotation: [Math.PI / 2, 0, 0] },
    ny: {
      position: [0, -distance, 0],
      rotation: [Math.PI / 2, 0, 0],
    },
    pz: { position: [0, 0, distance], rotation: [0, 0, 0] },
    nz: { position: [0, 0, -distance], rotation: [0, Math.PI, 0] },
  };

  if (!textures) return null;

  return (
    <>
      {SIDE.map((side) => (
        <MarkerFace
          key={side}
          side={side}
          imgUrl={textures ? textures[side] : null}
          transform={faceTransforms[side]}
          onClicked={onFaceClicked}
        />
      ))}
    </>
  );
};

export default MarkerComponent;
